import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signOut, getIdToken } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_BASE } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [role, setRole] = useState(null);

  const [loading, setLoading] = useState(true);

  // APPROVED | APPROVAL_PENDING | PAYMENT_REQUIRED | ONBOARDING_REQUIRED
  const [verificationStatus, setVerificationStatus] = useState(null);

  const verifyUserToken = useCallback(async (idToken) => {
    if (!idToken) return null;
    try {
      const res = await fetch(`${API_BASE}/auth/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const status = err.status || 'ERROR';
        setVerificationStatus(status);
        return null;
      }

      const data = await res.json();
      setTenant(data);
      setRole(data.role || 'USER');
      setVerificationStatus('APPROVED');
      return data;
    } catch (e) {
      setVerificationStatus('ERROR');
      return null;
    }
  }, []);

  const refreshProfile = useCallback(async (customToken) => {
    const activeToken = customToken || token;
    if (!activeToken && user) {
      const freshToken = await getIdToken(user, true);
      setToken(freshToken);
      return verifyUserToken(freshToken);
    }
    return verifyUserToken(activeToken);
  }, [token, user, verifyUserToken]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setToken(null);
        setTenant(null);
        setRole(null);
        setVerificationStatus(null);
        setLoading(false);
        return;
      }

      try {
        const idToken = await getIdToken(firebaseUser);
        setUser(firebaseUser);
        setToken(idToken);
        await verifyUserToken(idToken);
      } catch (e) {
        setVerificationStatus('ERROR');
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [verifyUserToken]);

  const logout = async () => {
    if (token) {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        tenant,
        role,
        loading,
        verificationStatus,
        isAdmin: role === 'ADMIN',
        isManager: role === 'MANAGER',
        isUser: role === 'USER',
        setTenant,
        setRole,
        setVerificationStatus,
        refreshProfile,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
